import { Trans } from 'react-i18next';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { PatientContinuouslyUsedMedicineExtended } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { Anchor, AnchorAlignment, EmptyStatePlaceholder, Table, TableKeyExtractor } from '@dreryk/react-components';

import { medicineSelectorTranslationKeys } from '../../../../../translations';
import { OnContinuouslyUsedMedicineSelection, OnMedicineSelection } from '../../../PrescriptionIssuingPanel.types';
import { MedicineSelectorTableDataAccessor } from '../MedicineSelectorTableDataAccessor';

import { ContinuouslyUsedMedicinePlaceholderIcon } from './ContinuouslyUsedMedicinePlaceholderIcon';
import {
   useContinuouslyUsedMedicineSelectorConfiguration,
   useContinuouslyUsedMedicineSelectorTableColumns,
} from './ContinuouslyUsedMedicineSelectorTable.hooks';

export type ContinuouslyUsedMedicineSelectorTableProps = {
   readonly patientId: Patient['id'];
   readonly onMedicineSelection: OnMedicineSelection;
   readonly onContinuouslyUsedMedicineSelection: OnContinuouslyUsedMedicineSelection;
   readonly visitDiagnoses: VisitDiagnosis[];
};

const Subheading = (): JSX.Element => (
   <Trans
      i18nKey={medicineSelectorTranslationKeys.table.noContinuouslyUsedMedicinesResults}
      components={{
         icon: <ContinuouslyUsedMedicinePlaceholderIcon />,
      }}
   />
);

export const keyExtractor: TableKeyExtractor<PatientContinuouslyUsedMedicineExtended> = (bean) => {
   return bean.patientContinuouslyUsedMedicine.id;
};

const ContinuouslyUsedMedicineSelectorTable = (props: ContinuouslyUsedMedicineSelectorTableProps): JSX.Element => {
   const dataConfiguration = useContinuouslyUsedMedicineSelectorConfiguration(props.patientId);
   const columns = useContinuouslyUsedMedicineSelectorTableColumns({
      patientId: props.patientId,
      onMedicineSelection: props.onMedicineSelection,
      onContinuouslyUsedMedicineSelection: props.onContinuouslyUsedMedicineSelection,
      onMedicineProcessed: dataConfiguration.refetch,
      visitDiagnoses: props.visitDiagnoses,
   });

   return (
      <>
         <Anchor
            shouldPerformScroll
            verticalAlignment={AnchorAlignment.START}
            testId={'continuouslyUsedMedicineSelector'}
         />
         <Table<PatientContinuouslyUsedMedicineExtended>
            columns={columns}
            dataConfiguration={dataConfiguration}
            keyExtractor={keyExtractor}
            dataAccessor={MedicineSelectorTableDataAccessor}
            placeholder={
               <EmptyStatePlaceholder
                  heading={medicineSelectorTranslationKeys.table.noContinuouslyUsedMedicines}
                  subheading={<Subheading />}
                  testId={'noContinuouslyUsedMedicines'}
               />
            }
            testId={'continuouslyUsedMedicines'}
         />
      </>
   );
};

export { ContinuouslyUsedMedicineSelectorTable };
